import { Avatar, Divider, Grid, Stack, Typography } from "@mui/material";
import { Buildings, UsersThree } from "@phosphor-icons/react";
import React, { useContext, useMemo } from "react";
import { AmbassadorProfileContext } from "..";
import { AppAlert } from "../../../../components/form";
import FlexBox from "../../../../components/layouts/Flexbox";
import { APP_COLORS } from "../../../../enums";
import { convertToFullName } from "../../../../services/utils";
import { useAppSelector } from "../../../../store";
import { InitiativeSelectableCard } from "../../../initiatives/InitiativeSelectableCard";
import { getNoOfEmpLabel } from "../../vendor-profile/forms/marketplace-form-drawer/marketplace/utils";
import Environment from "../environment";
import AmbassadorIntrestedIn from "../priorities";

export default function ProfileCard() {
  const {
    data,
    showIntrestedIn,
    limitedAccess = false,
    showInitiativesNote = false,
  } = useContext(AmbassadorProfileContext);
  const lists = useAppSelector((state) => state.lists);
  const hasProjects = data?.projects?.length > 0;

  const noOfEmpsList = useMemo(
    () => lists?.no_of_emps?.data || [],
    [lists?.no_of_emps?.data]
  );

  const employeeCountLabel = useMemo(() => {
    if (!data?.no_of_emp) return null;
    const label = getNoOfEmpLabel(noOfEmpsList, data.no_of_emp);
    return label || data.no_of_emp;
  }, [data?.no_of_emp, noOfEmpsList]);

  const getAvatarSrc = () => {
    if (!data) return null;
    return limitedAccess ? null : data?.photo || data?.picture;
  };

  const getAvatarInitial = () => {
    if (!data) return "";
    return limitedAccess ? data?.title?.[0] || "" : data?.firstName?.[0] || "";
  };

  const renderInfoCard = (
    icon: React.ReactNode,
    label: string,
    value: string | null | undefined,
    labelVariant: "small" | "small-ibmPlexMono" = "small-ibmPlexMono",
    withBorderRadius = false
  ) => {
    if (!value) return null;

    const renderIcon = ({ icon }: { icon: React.ReactNode }) => (
      <FlexBox
        alignItems="center"
        justifyContent="center"
        minHeight={44}
        minWidth={44}
        height={44}
        width={44}
        bgcolor={APP_COLORS.white}
        borderRadius={3}
      >
        {icon}
      </FlexBox>
    );

    const content = (
      <FlexBox gap={1.5}>
        {renderIcon({ icon })}
        <Stack gap={0.25}>
          <Typography variant={labelVariant} color={APP_COLORS.greyDark}>
            {label}
          </Typography>
          <Typography
            variant="body-ibmPlexMono-medium"
            color={APP_COLORS.textDark}
          >
            {value}
          </Typography>
        </Stack>
      </FlexBox>
    );

    return withBorderRadius ? (
      <FlexBox borderRadius={3}>{content}</FlexBox>
    ) : (
      content
    );
  };

  return (
    <Grid container>
      <Grid item xs={12} mb={3}>
        <FlexBox gap={1} alignItems="center">
          <Avatar src={getAvatarSrc()}>{getAvatarInitial()}</Avatar>
          <Stack>
            {limitedAccess ? null : (
              <Typography variant="bodyLarge-mono-medium">
                {convertToFullName(data)}
              </Typography>
            )}
            <Typography
              variant={limitedAccess ? "bodyLarge-mono-medium" : "small-mono"}
              color={limitedAccess ? APP_COLORS.text : APP_COLORS.textSecondary}
            >
              {data?.title}
            </Typography>
          </Stack>
        </FlexBox>
      </Grid>
      <Grid
        item
        xs={12}
        bgcolor={APP_COLORS.secondaryLight}
        p={2}
        borderRadius={3}
        container
        mb={3}
      >
        {limitedAccess ? null : (
          <Grid item xs={12} mb={2}>
            {renderInfoCard(<Buildings size={24} />, "Company", data?.company)}
          </Grid>
        )}
        <Grid item xs={12} sm={6} mb={{xs: 2, lg: 0}}>
          {renderInfoCard(
            <Buildings size={24} />,
            "Industry",
            data?.industry,
            "small",
            true
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          {renderInfoCard(
            <UsersThree size={24} />,
            "Employees",
            employeeCountLabel,
            "small-ibmPlexMono",
            true
          )}
        </Grid>
      </Grid>
      {(hasProjects || showInitiativesNote) && (
        <Grid item xs={12} mb={3}>
          <Stack gap={1}>
            <Typography variant="bodyLarge-mono-medium">Active Initiatives</Typography>
            {showInitiativesNote && (
              <AppAlert severity="info">
                The initiatives reflect broad priorities for the year and may not
                include every active initiative. If the profile is a good fit, send
                your questions to confirm current priorities.
              </AppAlert>
            )}
          </Stack>
          {hasProjects && (
            <>
              <Divider sx={{ marginTop: 0.5 }} />
              <Grid container spacing={2}>
                {data.projects.map((project: any) => (
                  <Grid item xs={12} key={project.id}>
                    <InitiativeSelectableCard initiative={project} />
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </Grid>
      )}
      <Grid item xs={12} mb={3}>
        <Environment />
      </Grid>
      {showIntrestedIn && data?.topics && data?.topics?.length > 0 && (
        <Grid item xs={12} mb={3}>
          <AmbassadorIntrestedIn />
        </Grid>
      )}
    </Grid>
  );
}
